import { useEffect, useRef } from 'react'
import { nudgeGame } from '@/shared/game/api'
import type { GameView } from '@/shared/game/types'
import { isLocalGame } from '@/shared/local/store'

/** Сколько ждать компьютера, прежде чем заподозрить, что его ход потерялся. */
const SILENCE_MS = 8000

/** Как часто проверять. Реже, чем ждём: спешить некуда. */
const CHECK_MS = 4000

/**
 * Присмотр за компьютером со стороны доски.
 *
 * Ход бота держится на одной джобе в очереди, и она может пропасть:
 * перезапустили воркера, обновился код, задание оказалось от прошлой версии
 * класса. На сервере это раз в минуту чинит games:nudge-bots, но минута —
 * вечность для того, кто смотрит на замершую доску. Поэтому клиент, заметив,
 * что ход за компьютером и на доске давно ничего не менялось, просит сервер
 * его разбудить.
 *
 * Повторный толчок безопасен: джоба сама проверяет, его ли ход.
 */
export function useBotWatchdog(gameId: string, view: GameView | undefined): void {
  const last = view?.feed[view.feed.length - 1]?.seq ?? 0

  // Что и когда происходило в партии в последний раз.
  const changed = useRef({ seq: last, at: Date.now() })

  const bots =
    view?.status === 'playing' &&
    view.players.find((player) => player.color === view.currentColor)?.bot !== null

  useEffect(() => {
    changed.current = { seq: last, at: Date.now() }
  }, [last])

  useEffect(() => {
    // Компьютер за одним устройством ходит здесь же, в браузере: будить
    // некого, а сервер про такую партию и не знает.
    if (bots !== true || isLocalGame(gameId)) return

    const timer = setInterval(() => {
      if (Date.now() - changed.current.at < SILENCE_MS) return

      // Считаем толчок событием: иначе он повторялся бы каждые CHECK_MS,
      // пока джоба доезжает.
      changed.current = { ...changed.current, at: Date.now() }
      void nudgeGame(gameId)
    }, CHECK_MS)

    return () => clearInterval(timer)
  }, [bots, gameId])
}
